<template>
  <div class="lite_popup lite_app_manage" v-show="show" @click.stop>
    <div class="lite_popup_box lite_app_manage_box">
      <div class="lite_popup_title">
        <span>{{ $t('language.liteApp.title') }}</span>
        <div class="icon white-close dialog-close" @click="close"></div>
      </div>
      <div class="lite_popup_content myscroll">
        <div v-if="!editing" class="lite_app_list">
          <div class="lite_app_toolbar">
            <span>{{ $t('language.liteApp.description') }}</span>
            <button @click="startCreate">{{ $t('language.liteApp.addApp') }}</button>
          </div>
          <div v-if="loading" class="lite_app_loading">{{ $t('language.common.load') }}</div>
          <div v-else-if="apps.length === 0" class="lite_app_empty">{{ $t('language.liteApp.empty') }}</div>
          <div v-else class="lite_app_item" v-for="app in apps" :key="app.id">
            <div class="lite_app_item_info">
              <div class="lite_app_item_name">
                <i class="lite_app_drive_icon" :class="'drive-icon-' + app.type"></i>
                <span>{{ cloudName(app.type) }} · {{ app.name }}</span>
              </div>
              <div class="lite_app_item_meta">{{ app.key }}<span v-if="app.inUse"> · {{ $t('language.liteApp.inUse') }}</span></div>
            </div>
            <div class="lite_app_actions">
              <button @click="startEdit(app)">{{ $t('language.liteApp.edit') }}</button>
              <button :disabled="app.inUse" @click="remove(app)">{{ $t('language.common.delete') }}</button>
            </div>
          </div>
        </div>
        <el-form v-else :model="draft" label-position="top" class="lite_app_form">
          <el-form-item :label="$t('language.liteApp.appType')">
            <div class="lite_app_type_select">
              <i class="lite_app_drive_icon" :class="'drive-icon-' + draft.type"></i>
              <el-select v-model="draft.type" :disabled="!!draft.id" class="lite_app_select">
                <el-option v-for="type in appTypes" :key="type" :label="cloudName(type)" :value="type">
                  <span class="lite_app_option">
                    <i class="lite_app_drive_icon" :class="'drive-icon-' + type"></i>
                    <span>{{ cloudName(type) }}</span>
                  </span>
                </el-option>
              </el-select>
            </div>
          </el-form-item>
          <el-form-item :label="$t('language.liteApp.appName')">
            <el-input v-model="draft.name" maxlength="255"></el-input>
          </el-form-item>
          <el-form-item :label="$t('language.liteApp.clientId')">
            <el-input v-model.trim="draft.key" :disabled="draft.inUse"></el-input>
          </el-form-item>
          <el-form-item :label="$t('language.liteApp.clientSecret')">
            <el-input v-model.trim="draft.secret" :type="showSecret ? 'text' : 'password'" autocomplete="new-password" :placeholder="draft.id ? $t('language.liteApp.secretUnchanged') : ''" :disabled="draft.inUse">
              <template #suffix>
                <div :class="showSecret ? 'open_eye' : 'close_eye'" @click="showSecret = !showSecret"></div>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item v-if="draft.callback" :label="$t('language.liteApp.callback')">
            <el-input class="lite_app_callback_input" :value="draft.callback" readonly>
              <i slot="suffix" class="copy lite_app_copy_icon" :title="$t('language.common.copy')" @click.stop="copyCallback"></i>
            </el-input>
          </el-form-item>
          <div v-if="draft.inUse" class="lite_app_used_tip">{{ $t('language.liteApp.usedTip') }}</div>
        </el-form>
      </div>
      <div class="lite_popup_footer" v-if="editing">
        <button class="lite_app_cancel" :disabled="saving" @click="cancelEdit">{{ $t('language.common.cancel') }}</button>
        <button :disabled="saving" @click="save">{{ $t('language.common.save') }}</button>
      </div>
    </div>
    <liteConfirm
      ref="deleteConfirm"
      :showCancel="true"
      :autoClose="false"
      :okText="$t('language.common.delete')"
      :data="deleteConfirmData"
      @ok="confirmRemove"
    />
  </div>
</template>

<script>
import { Message } from 'element-ui';
import requestUtils from '@/js/request_util.js';
import liteConfirm from '@/components/workspace/lite_confirm.vue';

export default {
  components: { liteConfirm },
  data() {
    return {
      show: false,
      loading: false,
      saving: false,
      editing: false,
      showSecret: false,
      apps: [],
      deleteAppId: '',
      deleteConfirmData: { title: '', desc: '' },
      originalKey: '',
      draft: this.emptyDraft(),
      appTypes: location.hostname === 'app.multcloud.com' ? ['GooglePhotos'] : [
        'GoogleWorkspace', 'GoogleDrive', 'OneDrive', 'Dropbox', 'PCloud', 'Box',
        'PutIO', 'FourShared', 'MediaFire', 'GooglePhotos', 'OneDriveBusiness',
        'DropboxBusiness', 'BoxBusiness', 'SharePoint', 'BaiDu', 'Flickr', 'Evernote',
        'SugarSync', 'Gmail', 'Outlook'
      ],
    };
  },
  methods: {
    emptyDraft() {
      return { id: '', type: 'GooglePhotos', name: '', key: '', secret: '', callback: '', inUse: false };
    },
    open() {
      this.show = true;
      this.editing = false;
      this.load();
    },
    close() {
      if (!this.saving) {
        this.show = false;
        this.editing = false;
      }
    },
    load() {
      this.loading = true;
      requestUtils.doLiteRequest('/app/config/list', {}).then(res => {
        this.loading = false;
        if (res.code === 200) {
          this.apps = res.data || [];
        } else {
          Message.error(this.errorMessage(res.msg || res.message));
        }
      }, err => {
        this.loading = false;
        Message.error(err);
      });
    },
    startCreate() {
      this.originalKey = '';
      this.showSecret = false;
      this.draft = this.emptyDraft();
      this.editing = true;
      this.loadCallback(this.draft.type);
    },
    startEdit(app) {
      this.originalKey = app.key || '';
      this.showSecret = false;
      this.draft = {
        id: app.id,
        type: app.type,
        name: app.name,
        key: app.key || '',
        secret: '',
        callback: this.currentCallback(),
        inUse: !!app.inUse,
      };
      this.editing = true;
    },
    cancelEdit() {
      this.editing = false;
      this.showSecret = false;
      this.draft = this.emptyDraft();
    },
    loadCallback(type) {
      this.draft.callback = this.currentCallback();
      requestUtils.doLiteRequest('/app/config/available', { type: type }).then(res => {
        if (res.code === 200 && this.editing && !this.draft.id && this.draft.type === type) {
          this.draft.callback = (res.data || {}).callback || this.currentCallback();
        }
      });
    },
    currentCallback() {
      switch (location.hostname) {
        case 'test.multcloud.com':
          return 'https://api.multcloud.com/drives/callback';
        case 'app.multcloud.com':
          return 'https://api2.multcloud.com/drives/callback';
        default:
          return 'https://localhost/drives/callback';
      }
    },
    copyCallback() {
      const callback = this.draft.callback;
      if (!callback) return;
      const copied = () => Message.success(this.$t('language.liteApp.copySuccess'));
      const fallback = () => {
        const textarea = document.createElement('textarea');
        textarea.value = callback;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          copied();
        } catch (e) {
          Message.error(this.$t('language.common.failure'));
        } finally {
          document.body.removeChild(textarea);
        }
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(callback).then(copied, fallback);
      } else {
        fallback();
      }
    },
    save() {
      if (!this.draft.name || !this.draft.name.trim()) {
        Message.warning(this.$t('language.liteApp.nameRequired'));
        return;
      }
      const creating = !this.draft.id;
      const payload = {
        id: this.draft.id,
        type: this.draft.type,
        name: this.draft.name,
        key: this.draft.key,
        secret: this.draft.secret,
      };
      if (creating && (!payload.key || !payload.secret)) {
        Message.warning(this.$t('language.liteApp.credentialsRequired'));
        return;
      }
      if (!creating && !payload.secret) {
        if (payload.key !== this.originalKey) {
          Message.warning(this.$t('language.liteApp.secretRequiredForKey'));
          return;
        }
        // 空密钥表示只改名称，后端会保留既有凭据。
        payload.key = '';
      }
      this.saving = true;
      requestUtils.doLiteRequest(creating ? '/app/config/create' : '/app/config/update', payload).then(res => {
        this.saving = false;
        if (res.code === 200) {
          this.editing = false;
          this.draft = this.emptyDraft();
          this.load();
        } else {
          Message.error(this.errorMessage(res.msg || res.message));
        }
      }, err => {
        this.saving = false;
        Message.error(err);
      });
    },
    remove(app) {
      if (app.inUse) return;
      this.deleteAppId = app.id;
      this.deleteConfirmData.title = this.$t('language.liteApp.warning');
      this.deleteConfirmData.desc = this.$t('language.liteApp.deleteConfirm');
      this.$refs.deleteConfirm.show = true;
    },
    confirmRemove() {
      const confirm = this.$refs.deleteConfirm;
      if (!this.deleteAppId || confirm.loading) return;
      confirm.loading = true;
      requestUtils.doLiteRequest('/app/config/delete', { id: this.deleteAppId }).then(res => {
        confirm.loading = false;
        if (res.code === 200) {
          confirm.show = false;
          this.deleteAppId = '';
          this.load();
        } else {
          Message.error(this.errorMessage(res.msg || res.message));
        }
      }, err => {
        confirm.loading = false;
        Message.error(err);
      });
    },
    errorMessage(message) {
      const messages = {
        app_in_use: this.$t('language.liteApp.appInUse'),
        app_name_exists: this.$t('language.liteApp.nameExists'),
        app_key_secret_required: this.$t('language.liteApp.credentialsRequired'),
        system_no_default_app: this.$t('language.liteApp.systemAppUnavailable'),
      };
      return messages[message] || message || this.$t('language.common.failure');
    },
    cloudName(type) {
      const names = {
        GoogleWorkspace: 'Google Workspace', GoogleDrive: 'Google Drive', OneDrive: 'OneDrive',
        Dropbox: 'Dropbox', PCloud: 'pCloud', Box: 'Box', PutIO: 'Put.io',
        FourShared: '4shared', MediaFire: 'MediaFire', GooglePhotos: 'Google Photos',
        OneDriveBusiness: 'OneDrive for Business', DropboxBusiness: 'Dropbox Business',
        BoxBusiness: 'Box for Business', SharePoint: 'SharePoint Online', BaiDu: 'Baidu',
        Flickr: 'Flickr', Evernote: 'Evernote', SugarSync: 'SugarSync', Gmail: 'Gmail', Outlook: 'Outlook',
      };
      return names[type] || type;
    },
  },
  watch: {
    'draft.type'(type) {
      if (this.editing && !this.draft.id) {
        this.loadCallback(type);
      }
    },
  },
};
</script>

<style scoped>
.lite_app_manage_box {
  width: 780px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.lite_app_manage_box > .lite_popup_title {
  min-height: 40px;
  height: auto;
  padding: 10px 16px 10px 20px;
  display: flex;
  align-items: flex-start;
  flex: 0 0 auto;
  box-sizing: border-box;
  line-height: 20px;
}
.lite_app_manage_box > .lite_popup_title > span {
  min-width: 0;
  padding-right: 12px;
  flex: 1;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.lite_app_manage_box > .lite_popup_title .dialog-close {
  float: none;
  position: static;
  flex: 0 0 auto;
}
.lite_app_manage_box > .lite_popup_content {
  min-height: 0;
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}
.lite_app_manage_box > .lite_popup_footer {
  padding-top: 12px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  flex-wrap: wrap;
  flex: 0 0 auto;
  box-sizing: border-box;
}
.lite_app_manage_box > .lite_popup_footer > button {
  max-width: 100%;
  min-height: 30px;
  height: auto;
  margin: 4px 0 4px 10px;
  padding: 5px 14px !important;
  line-height: 18px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.lite_app_toolbar {
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  color: #6f7886;
}
.lite_app_toolbar > span {
  min-width: 0;
  flex: 1;
  line-height: 20px;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.lite_app_toolbar button,
.lite_app_actions button {
  border: 0;
  background: none;
  color: #5267bf;
  cursor: pointer;
}
.lite_app_toolbar button {
  max-width: 45%;
  min-height: 30px;
  height: auto;
  margin-left: 16px;
  padding: 6px 14px !important;
  flex: 0 0 auto;
  color: #fff;
  background: #5267bf;
  border-radius: 3px;
  line-height: 18px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.lite_app_item {
  min-height: 58px;
  padding: 12px 8px;
  display: flex;
  align-items: center;
  border-top: 1px solid #edf0f6;
  box-sizing: border-box;
}
.lite_app_item_info {
  min-width: 0;
  flex: 1;
}
.lite_app_item_name {
  display: flex;
  align-items: flex-start;
  color: #333;
  font-size: 14px;
  line-height: 20px;
}
.lite_app_item_name > span {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.lite_app_item_meta {
  margin-top: 7px;
  color: #9aa3b4;
  font-size: 12px;
  line-height: 18px;
  overflow-wrap: anywhere;
  word-break: break-all;
}
.lite_app_actions {
  max-width: 45%;
  margin-left: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  flex: 0 0 auto;
  flex-wrap: wrap;
}
.lite_app_actions button {
  max-width: 100%;
  min-height: 30px;
  height: auto;
  margin: 3px 0 3px 8px;
  padding: 5px 8px !important;
  line-height: 18px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.lite_app_actions button:disabled { color: #c9cfd9; cursor: not-allowed; }
.lite_app_empty,
.lite_app_loading { padding: 45px 0; color: #9aa3b4; text-align: center; line-height: 20px; overflow-wrap: anywhere; }
.lite_app_select,
.lite_app_form >>> .el-input { width: 100%; }
.lite_app_type_select { position: relative; width: 100%; }
.lite_app_type_select>.lite_app_drive_icon {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 12px;
  margin-top: -4px;
  pointer-events: none;
}
.lite_app_type_select >>> .el-input__inner { padding-left: 42px; }
.lite_app_drive_icon { display: inline-block; width: 18px; height: 18px; flex: 0 0 18px; margin-right: 8px; background-position: center; background-repeat: no-repeat; background-size: contain; }
.lite_app_option { display: flex; align-items: center; height: 34px; }
.lite_app_option .lite_app_drive_icon { width: 18px; height: 18px; }
.lite_app_callback_input >>> .el-input__inner { padding-right: 38px; }
.lite_app_copy_icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-top: 22px;
  margin-right: 10px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  cursor: pointer;
  opacity: .75;
}
.lite_app_copy_icon:hover { opacity: 1; }
.lite_app_form >>> .el-form-item {
  display: block;
  margin-bottom: 18px;
}
.lite_app_form >>> .el-form-item__label {
  width: auto !important;
  min-height: 20px;
  height: auto;
  padding: 0px;
  float: none;
  display: block;
  box-sizing: border-box;
  text-align: left;
  line-height: 20px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.lite_app_form >>> .el-form-item__content {
  width: 100%;
  margin-left: 0 !important;
  line-height: normal;
}
.lite_app_form >>> .open_eye,
.lite_app_form >>> .close_eye {
  width: 30px;
  height: 30px;
  margin-top: 15px;
  display: inline-block;
  vertical-align: top;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 20px;
  cursor: pointer;
}
.lite_app_form >>> .open_eye { background-image: url("../../../assets/images/lite/icon/open_eye.svg"); }
.lite_app_form >>> .close_eye { background-image: url("../../../assets/images/lite/icon/close_eye.svg"); }
.lite_app_used_tip {
  margin: -2px 0 12px;
  color: #e6a23c;
  font-size: 12px;
  line-height: 20px;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.lite_app_cancel { background: #fff !important; color: #5267bf !important; border: 1px solid #5267bf !important; }
.lite_app_form >>> .el-input__icon {
  height: 40px;
  line-height: 40px;
  position: relative;
  top: 8px;
}

@media (max-width: 600px) {
  .lite_app_manage_box {
    max-width: calc(100vw - 16px);
    max-height: calc(100vh - 16px);
  }
  .lite_app_manage_box > .lite_popup_content { padding: 16px 12px; }
  .lite_app_manage_box > .lite_popup_footer { padding: 12px; }
  .lite_app_toolbar { display: block; }
  .lite_app_toolbar button {
    width: 100%;
    max-width: none;
    margin: 12px 0 0;
  }
  .lite_app_item {
    display: block;
    padding: 12px 4px;
  }
  .lite_app_actions {
    max-width: none;
    margin: 8px 0 0;
  }
  .lite_app_manage_box > .lite_popup_footer > button {
    min-width: 120px;
    flex: 1 1 120px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/popup/personal_center_popups/lite_app_manage_popup.vue